import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import SEOHead from '@/components/SEOHead';
import PricingPlans from '@/components/PricingPlans';
import { MarketingShell, PageHero, PageSection, FaqList, ClosingCta, buildFaqJsonLd, buildBreadcrumb } from '@/components/marketing/MarketingShell';

const FAQ = [
  {
    q: 'Berapa harga Nerava per bulan?',
    a: 'Paket Starter Rp 199.000 per bulan, Growth Rp 449.000 per bulan, dan Business Rp 999.000 per bulan. Paket tahunan hanya dibayar 10 bulan untuk 12 bulan pemakaian. Semua paket sudah termasuk WhatsApp, Instagram, Facebook Messenger, dan AI agent.',
  },
  {
    q: 'Apakah ada versi gratis?',
    a: 'Ada. Trial 30 hari dengan akses penuh ke semua fitur, tanpa kartu kredit. Anda bisa menghubungkan channel, melatih AI dengan katalog produk, dan memproses pesanan sungguhan selama masa trial.',
  },
  {
    q: 'Apakah ada biaya tersembunyi?',
    a: 'Tidak ada. Harga paket sudah mencakup semua channel dan fitur AI. Biaya tambahan hanya muncul bila Anda memakai kuota di luar paket, dan jumlahnya terlihat jelas di dashboard sebelum dipakai.',
  },
  {
    q: 'Bisakah pindah paket di tengah jalan?',
    a: 'Bisa. Upgrade berlaku langsung dan sisa masa paket lama diperhitungkan. Downgrade berlaku pada periode penagihan berikutnya.',
  },
  {
    q: 'Metode pembayaran apa yang didukung?',
    a: 'Transfer bank, virtual account, e-wallet, dan QRIS. Invoice otomatis dikirim ke email setiap kali pembayaran berhasil.',
  },
];

const JSON_LD = [
  buildBreadcrumb('Harga', '/harga'),
  buildFaqJsonLd(FAQ),
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Nerava',
    description: 'CRM omnichannel dengan AI customer assistant untuk toko online dan UMKM Indonesia.',
    brand: { '@type': 'Brand', name: 'Nerava' },
    offers: [
      { '@type': 'Offer', name: 'Starter', price: '199000', priceCurrency: 'IDR', url: 'https://nerava.id/harga' },
      { '@type': 'Offer', name: 'Growth', price: '449000', priceCurrency: 'IDR', url: 'https://nerava.id/harga' },
      { '@type': 'Offer', name: 'Business', price: '999000', priceCurrency: 'IDR', url: 'https://nerava.id/harga' },
    ],
  },
];

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSelect = (planId, billingPeriod = 'monthly') => {
    if (planId === 'enterprise') {
      navigate('/tentang-kami');
      return;
    }
    navigate(user ? `/payment?plan=${planId}&billing=${billingPeriod}` : '/register');
  };

  return (
    <MarketingShell active="/harga">
      <SEOHead
        title="Harga Nerava: Paket AI Customer Service & CRM Mulai Rp 199.000"
        description="Lihat paket Nerava: AI agent 24/7, inbox WhatsApp, Instagram & Messenger, CRM, dan manajemen pesanan. Mulai Rp 199.000/bulan. Coba gratis 30 hari, tanpa kartu kredit."
        canonical="https://nerava.id/harga"
        jsonLd={JSON_LD}
      />
      <PageHero
        eyebrow="Paket Harga"
        title="Harga jelas, semua channel dan"
        highlight="AI sudah termasuk"
        subtitle="Satu langganan untuk inbox omnichannel, AI customer assistant, CRM, dan manajemen pesanan. Mulai dari trial gratis 30 hari, lanjut hanya kalau memang terasa manfaatnya."
        bullets={['Tanpa kartu kredit', 'Semua channel di setiap paket', 'Bayar tahunan hemat 2 bulan']}
      />

      <div data-testid="pricing-plans-public">
        <PricingPlans onSelectPlan={handleSelect} currentPlan={user?.plan} />
      </div>

      <PageSection
        title="Apa yang Anda dapat di semua paket"
        intro="Tidak ada fitur inti yang dikunci di paket mahal. Perbedaan paket ada pada kapasitas, bukan pada kemampuan."
        tinted
      >
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-slate-600 dark:text-zinc-300">
          {[
            'Inbox gabungan WhatsApp, Instagram, Facebook Messenger, dan website',
            'AI customer assistant yang menjawab dari katalog produk Anda sendiri',
            'Katalog produk dengan foto, varian, stok, dan harga',
            'Manajemen pesanan dari chat sampai bukti transfer terverifikasi',
            'Database pelanggan lengkap dengan riwayat pembelian',
            'Invoice otomatis dan laporan penjualan',
          ].map((item) => (
            <li key={item} className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/60">
              {item}
            </li>
          ))}
        </ul>
      </PageSection>

      <PageSection title="Pertanyaan soal harga">
        <FaqList items={FAQ} />
        <p className="mt-8 text-sm text-slate-500 dark:text-zinc-400">
          Masih ragu paket mana yang cocok?{' '}
          <Link to="/tentang-kami" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="pricing-contact-link">
            Hubungi tim Nerava
          </Link>{' '}
          dan ceritakan volume chat Anda.
        </p>
      </PageSection>

      <ClosingCta
        title="Coba dulu 30 hari, baru putuskan"
        subtitle="Hubungkan channel, latih AI dengan produk Anda, dan lihat sendiri berapa chat yang bisa dijawab tanpa Anda pegang HP."
        showPricingCta={false}
      />
    </MarketingShell>
  );
};

export default PricingPage;
